/*
 * Amada conversions
 *
 * Small adapter on top of amada-tracking.js. It listens for the canonical
 * `amada:tracking` events that script already emits and:
 *   1. reports real outcomes to Google Ads through the Google tag on the page,
 *   2. forwards them to our server (tracking-browser), which sends them to OpenAI,
 *   3. opens Calendly as an on-site popup instead of a new tab, so a completed
 *      booking is visible to the page ("calendly.event_scheduled").
 * Google call tracking swaps the shown phone numbers for ad visitors only.
 * No secrets live here: Google labels are public, the endpoint takes no key.
 */
(function amadaConversions() {
  'use strict';

  if (window.__amadaConversionsLoaded) return;
  if (window.Shopify && window.Shopify.designMode) return;
  window.__amadaConversionsLoaded = true;

  const GOOGLE_ADS_ID = 'AW-10902940604';
  // Conversion labels from Google Ads (the part after the "/" in send_to). Empty = off.
  const GOOGLE_LABELS = {
    whatsapp_click: 'OKGzCO-L7ZIdELzX9s4o',
    lead_created: 'TZH7CN2f65IdELzX9s4o',
    appointment_client_confirmed: 'HtgvCKvu65IdELzX9s4o',
  };
  let GOOGLE_CALL_LABEL = 'wlxBCNWc7ZIdELzX9s4o';
  // Optional page-level override (staging / tests): window.AmadaConversionsConfig.
  const override = window.AmadaConversionsConfig || {};
  if (override.googleLabels) Object.assign(GOOGLE_LABELS, override.googleLabels);
  if (typeof override.callLabel === 'string') GOOGLE_CALL_LABEL = override.callLabel;
  // Must match the text amada-phone-hours.js shows in the daytime (10:00-17:59 London).
  const CALL_NUMBER = '+44 2079165682';

  const LEDGER_ENDPOINT =
    override.ledgerEndpoint || 'https://tykolwlxynqulaqqhomv.supabase.co/functions/v1/tracking-browser';
  // Only these reach the server. A client-side form success is not an authoritative
  // lead, so it is recorded as form_success_client and the server maps it for OpenAI.
  const LEDGER_EVENTS = {
    whatsapp_click: 'whatsapp_click',
    phone_click: 'phone_click',
    email_click: 'email_click',
    lead_created: 'form_success_client',
    appointment_client_confirmed: 'appointment_client_confirmed',
  };

  const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';
  const CALENDLY_STYLE = 'https://assets.calendly.com/assets/external/widget.css';
  const CALENDLY_LOAD_TIMEOUT_MS = 5000;

  function marketingAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function') return false;
    try {
      return Boolean(privacy.marketingAllowed());
    } catch (error) {
      return false;
    }
  }

  function reportToGoogle(label, eventId) {
    if (!label || typeof window.gtag !== 'function') return;
    try {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${label}`,
        transaction_id: eventId,
      });
    } catch (error) {
      // Measurement must never break the page.
    }
  }

  function sendToLedger(envelope) {
    const name = LEDGER_EVENTS[envelope.event_name];
    if (!name || !marketingAllowed()) return;
    const payload = Object.assign({}, envelope.payload);
    if (name !== envelope.event_name) payload.client_event_name = envelope.event_name;
    const body = JSON.stringify(Object.assign({}, envelope, { event_name: name, payload }));

    try {
      // text/plain keeps this a CORS "simple" request (no credentialed preflight).
      if (navigator.sendBeacon && navigator.sendBeacon(LEDGER_ENDPOINT, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) {
        return;
      }
    } catch (error) {
      // Fall through to fetch.
    }
    try {
      fetch(LEDGER_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
    } catch (error) {
      // Ignore.
    }
  }

  document.addEventListener('amada:tracking', (event) => {
    const envelope = event.detail;
    if (!envelope || !envelope.event_name || !envelope.event_id) return;
    reportToGoogle(GOOGLE_LABELS[envelope.event_name], envelope.event_id);
    sendToLedger(envelope);
  });

  // Google "calls from website": for ad visitors Google provides a forwarding number
  // and counts real calls to it. amada-phone-hours.js rewrites phone numbers every
  // minute, so Google must not swap the DOM itself; the callback hands the number to
  // phone-hours, which shows it in place of the daytime number.
  function configureCallTracking() {
    if (!GOOGLE_CALL_LABEL || typeof window.gtag !== 'function') return;
    // Visitors in a market with its own phone line (the US) never see the UK number Google would swap.
    if (document.documentElement.dataset.amadaPhone) return;
    try {
      window.gtag('config', `${GOOGLE_ADS_ID}/${GOOGLE_CALL_LABEL}`, {
        phone_conversion_number: CALL_NUMBER,
        phone_conversion_callback: (formattedNumber, plainNumber) => {
          const digits = String(plainNumber || formattedNumber || '').replace(/\D/g, '');
          if (!formattedNumber || !digits) return;
          window.AmadaPhoneOverride = { display: String(formattedNumber), href: `tel:+${digits}` };
          document.dispatchEvent(new CustomEvent('amada:phone-override'));
        },
      });
    } catch (error) {
      // Ignore.
    }
  }
  if (document.readyState === 'complete') configureCallTracking();
  else window.addEventListener('load', configureCallTracking, { once: true });

  // Calendly popup -----------------------------------------------------------
  let calendlyLoading = null;

  function loadCalendly() {
    if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') return Promise.resolve();
    if (calendlyLoading) return calendlyLoading;
    calendlyLoading = new Promise((resolve, reject) => {
      if (!document.querySelector(`link[href="${CALENDLY_STYLE}"]`)) {
        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = CALENDLY_STYLE;
        document.head.appendChild(style);
      }
      const script = document.createElement('script');
      script.src = CALENDLY_SCRIPT;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        calendlyLoading = null;
        reject(new Error('calendly_unavailable'));
      };
      document.head.appendChild(script);
      setTimeout(() => {
        if (window.Calendly && typeof window.Calendly.initPopupWidget === 'function') return;
        calendlyLoading = null;
        reject(new Error('calendly_timeout'));
      }, CALENDLY_LOAD_TIMEOUT_MS);
    });
    return calendlyLoading;
  }

  function isCalendlyHref(href) {
    try {
      const host = new URL(href, window.location.href).hostname;
      return host === 'calendly.com' || host.endsWith('.calendly.com');
    } catch (error) {
      return false;
    }
  }

  function calendlyUrl(href) {
    const tracking = window.AmadaTracking;
    if (tracking && typeof tracking.enrichCalendlyUrl === 'function') {
      try {
        return tracking.enrichCalendlyUrl(href);
      } catch (error) {
        return href;
      }
    }
    return href;
  }

  // Window capture phase: runs before any page script can navigate the click away.
  window.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !isCalendlyHref(link.href)) return;
    // The inline calendar section has its own fallback link; leave it alone.
    if (link.closest('.amada-booking__calendly')) return;

    event.preventDefault();
    const url = calendlyUrl(link.href);
    loadCalendly()
      .then(() => window.Calendly.initPopupWidget({ url }))
      .catch(() => {
        window.location.href = url;
      });
  }, true);

  // Warm the widget so the first click opens instantly.
  const warm = () => {
    if (document.querySelector('a[href*="calendly.com"]')) loadCalendly().catch(() => {});
  };
  if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 4000 });
  else setTimeout(warm, 2000);
})();
